<script lang="ts">
  // services
  import { DistributorEntity, ErrorMessageEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import Flash from "@shared/services/flashv2.js";
  import SalesforceChat from "@shared/services/salesforce_chat";

  // components
  import Autocomplete from "@shared/svelte/Components/Autocomplete.svelte";
  import FlashWrapper from "@shared/svelte/Components/Flash/Wrapper.svelte";
  import FormStepper from "@onboarding2/svelte/Shared/FormStepper.svelte";
  import Header from "@onboarding2/svelte/Shared/Header.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";
  import StepperFooter from "@onboarding2/svelte/Shared/StepperFooter.svelte";

  // stores
  import RetailerStore from "@onboarding2/svelte/Stores/RetailerStore";

  // types
  import type { Distributor } from "@shared/types/Distributor";
  import type { RetailerType } from "@shared/types/Retailer";

  export let retailers: RetailerType[];

  const { trackClick, trackEvent, immediatelyProcessEventQueue } = EventTracking;

  const retailerStore = RetailerStore(
    retailers[0],
    "/api/retailer?include_distributors=1",
    "/api/onboarding/update",
  );

  let retailer = $retailerStore;
  let selectedDistributors: Distributor[] = [];
  let submitting: boolean = false;

  const selectDistributor = (e: CustomEvent): void => {
    if (typeof e.detail == "object" && !selectedDistributors.find(d => d.name === e.detail.name)) {
      trackClick("onboarding_distributor_select", DistributorEntity.fromDistributor(e.detail));
      selectedDistributors = [e.detail, ...selectedDistributors];
    }
  };

  const removeDistributor = (distributor: Distributor): void => {
    selectedDistributors = selectedDistributors.filter(
      distributors => distributors.name !== distributor.name,
    );
  };

  const goHome = async (): Promise<void> => {
    await immediatelyProcessEventQueue();
    window.location.reload();
  };

  const goNext = async (): Promise<void> => {
    window["stateService"].go("layout.signedIn.salesrep", window["stateService"].params);
  };

  const save = async (
    retailer: RetailerType & { distributor_ids?: number[] },
    successCallbackName: string,
  ): Promise<void> => {
    if (selectedDistributors.length < 1 && successCallbackName !== "goHome") {
      trackEvent("begin_to_render", [
        new ErrorMessageEntity({ messages: ["To continue, add at least 1 distributor"] }),
      ]);

      return Flash.now({ type: "error", message: "To continue, add at least 1 distributor" });
    }

    submitting = true;
    retailer.distributor_ids = selectedDistributors.map(d => d.id);
    retailerStore
      .save(retailer)
      .then(() => {
        if (successCallbackName == "goHome") goHome();
        else if (successCallbackName == "goNext") goNext();
      })
      .catch(() => {
        trackEvent("begin_to_render", [
          new ErrorMessageEntity({
            messages: [`Error saving, would have then ${successCallbackName}`],
          }),
        ]);
        submitting = false;
      });
  };

  const evaluateEnter = (e: CustomEvent): void => {
    if (e.detail == "") save(retailer, "goNext");
  };

  setTimeout(() => {
    SalesforceChat.hide();
  }, 1000);

  $: selectedDistributors = retailer.directly_added_distributors;
</script>

<PageTitle title="Your distributors - Sign up | Provi" />
<div class="fizz-layout-1-col-narrow-header-footer" style="min-height: 100vh;">
  <Header title={"Account setup"}>
    <FormStepper currentStep={2} />
  </Header>
  <main data-grid-area="main">
    <h2 class="fizz-heading-3 fizz-stack-8 fizz-text-center">
      Which distributors do you order from?
    </h2>
    <p class="fizz-stack-32 fizz-text-center fizz-text-medium">
      You can order from all of your distributors on Provi.
    </p>
    <form class="fizz-flow-16 fizz-stack-24">
      <div>
        <label class="fizz-label fizz-stack-4" for="search">Find your distributors</label>
        <Autocomplete
          displayTags={false}
          inputId="search"
          listMode={true}
          objectType="distributor"
          placeholder="Search distributors"
          searchUrlInput="/api/retailer/distributors/search"
          on:focus={() => trackClick("onboarding_autocomplete_distributors_field")}
          on:keyenter={evaluateEnter}
          on:tagAdded={selectDistributor}
        />
      </div>
    </form>
    {#each selectedDistributors as distributor}
      <div
        class="fizz-card fizz-radius-medium fizz-bordered fizz-inset-square-16 fizz-flex-row-8 fizz-stack-8"
      >
        <Icon type="TruckCircle" height="32" width="32" extraClassNames="color-brandSecondary" />
        <strong class="fizz-text-medium fizz-flex-item">{distributor.name}</strong>
        <Icon
          type="X"
          height="12"
          width="12"
          onPress={() => removeDistributor(distributor)}
          extraClassNames="cursor-pointer"
          dataTestId="unselect-distributor-{distributor.id}"
        />
      </div>
    {/each}
  </main>
  <StepperFooter
    backState="layout.signedIn.detailsManual"
    disabled={submitting}
    nextFn={() => save(retailer, "goNext")}
    saveFn={() => save(retailer, "goHome")}
  />
</div>
<FlashWrapper />
